import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus } from 'react-icons/fa';
import { employeesApi } from '../../services/api';
import { useAlert } from '../../contexts/AlertContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Can from '../common/Can';
import { ACTIONS, SUBJECTS } from '../../constants/permissions';
import { BackButton } from '../common/styles';
import useEmployeesStoreRepository from '../../store/employeesStoreRepository';
import useEmployeesRepositorySearch from '../../hooks/useEmployeesRepositorySearch';
import useGridNavigation from '../../hooks/useGridNavigation';
import EmployeeRepositoryFilters from './components/EmployeeRepositoryFilters';
import EmployeeGrid from './components/EmployeeGrid';
import EmployeeForm from './EmployeeForm';
import { INDIVIDUAL_OPEN_MODES, INDIVIDUAL_PAGE_MODES } from '../../constants/individualOpenModes';
import EmployeePositionHistory from './EmployeePositionHistory';
import EmployeeRoleHistory from './EmployeeRoleHistory';
import ResultsCount from '../common/ResultsCount';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  ControlsContainer,
  MainContainer,
  HeaderButton,
} from './styles';
// ADR-113 §5 — the shared contract-scoped lifecycle modals (one
// implementation per verb; the Contracts tab is their authoritative home,
// these card shortcuts are the other mount). The per-verb modal state and
// submit logic that used to live here moved into the shared component.
import ContractActionModals from './components/ContractActionModals';
import { PERSON_ACTIONS } from '../../constants/contractActions';

const EmployeesRepository = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { id } = useParams(); // Get the employee ID from URL params
  const [showFilters, setShowFilters] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  // ADR-113 §5 — which shared lifecycle modal a card entry opened
  // (a PERSON_ACTIONS value since ADR-158, or null). The modals' own state
  // lives in ContractActionModals.
  const [contractAction, setContractAction] = useState(null);
  
  const {
    searchInputValue,
    setSearchInputValue,
    selectedPositions,
    setSelectedPositions,
    selectedGrades,
    setSelectedGrades,
    selectedProjects,
    setSelectedProjects,
    selectedStatuses,
    setSelectedStatuses,
    selectedCluster,
    setSelectedCluster,
    reportingScope,
    setReportingScope,
    clearSearchFilters
  } = useEmployeesRepositorySearch();
  const gridRef = useRef(null);
  
  // Get state and actions from Repository store
  const {
    employees,
    isLoading,
    isLoadingMore,
    pageInfo,
    fetchEmployeesWithReset,
    fetchEmployeesLoadMore,
    setIsReturningFromDetails,
    resetState,
    resetFilters,
    updateEmployee
  } = useEmployeesStoreRepository();

  const loadMoreCallback = useCallback(() => {
    return fetchEmployeesLoadMore();
  }, [fetchEmployeesLoadMore]);

  // Callback to clean up filters when navigating away from Employees Repository
  const handleNavigateAway = useCallback(() => {
    clearSearchFilters();
    resetFilters();
  }, [clearSearchFilters, resetFilters]);

  // Use the GridNavigation hook with repository routes
  const {
    showLoadingSpinner,
    handleCombinedGridScroll,
    handleItemClick,
    handleBackToItems,
    handleAddNewItem,
    handleCustomBack,
    shouldHideContent,
    loadMoreData,
    isNewItem,
    shouldLoadData,
    shouldForceReload,
    isReturningFromDetailPage,
    hasItems,
    navigateWithScrollState
  } = useGridNavigation({
    gridRef,
    items: employees,
    columnCount: 4, // Employee cards are wider, so 2 columns
    rowHeight: 450, // Employee cards are taller
    routes: {
      list: '/employees',
      detail: (id) => `/employees/${id}/edit`,
      new: '/employees/new',
      additionalRoutes: [
        { path: '/edit/rehire', shouldLoadData: false },
        { path: '/edit/replace', shouldLoadData: false },
        { path: '/view', shouldLoadData: false }
      ]
    },
    onReturnFromDetail: () => {
      setIsReturningFromDetails(true);
    },
    loadMoreDataCallback: loadMoreCallback,
    pageInfo,
    isLoadingMore,
    onNavigateAway: handleNavigateAway
  });

  // First effect: Always reset the store on mount
  useEffect(() => {
    resetState();
  }, [resetState]); // Run whenever resetState changes or component mounts

  // Second effect: Load data when needed
  useEffect(() => {
    // Only run this effect when we're on the main repository view
    // using the centralized shouldLoadData function
    if (!shouldLoadData()) {
      return;
    }

    // Check if we should force reload (e.g., coming back from rehire) BEFORE
    // the has-items early return below. ADR-136 §15 bug (found 2026-08-07):
    // this used to be computed after that return, so a navigate('/employees',
    // {state: {forceRefresh: true, fromRehire: true}}) from the rehire /
    // replace / schedule flows never actually refetched when the grid already
    // had items — the card menus then gated verbs (Cancel Scheduled Renewal,
    // Add End Date, Replace Contract) on a stale pendingRenewal.
    const shouldRefreshFromRehire = location.state?.forceRefresh && location.state?.fromRehire;

    // CRITICAL FIX: Prevent reloading data if we already have items
    // This prevents infinite reload cycles when using cursor-based pagination
    if (hasItems && !isLoadingMore && !shouldForceReload() && !shouldRefreshFromRehire) {
      return;
    }

    const forceReload = shouldForceReload() || shouldRefreshFromRehire;

    // Fetch data with reset parameter based on whether we need to force reload
    fetchEmployeesWithReset(forceReload);

    // Clear the state after reload to prevent repeated reloads
    if (shouldRefreshFromRehire) {
      navigate(location.pathname, { replace: true, state: {} });
    }

  // Removed employees.length from dependencies to prevent data reload when pagination adds items
  }, [fetchEmployeesWithReset, shouldLoadData, shouldForceReload, isReturningFromDetailPage, hasItems, isLoadingMore, location.state]);


  // Render filters section
  const renderFilters = () => {
    return <EmployeeRepositoryFilters
      searchInputValue={searchInputValue}
      setSearchInputValue={setSearchInputValue}
      selectedPositions={selectedPositions}
      setSelectedPositions={setSelectedPositions}
      selectedGrades={selectedGrades}
      setSelectedGrades={setSelectedGrades}
      selectedProjects={selectedProjects}
      setSelectedProjects={setSelectedProjects}
      selectedStatuses={selectedStatuses}
      setSelectedStatuses={setSelectedStatuses}
      selectedCluster={selectedCluster}
      setSelectedCluster={setSelectedCluster}
      reportingScope={reportingScope}
      setReportingScope={setReportingScope}
    />;
  };

  // Render employees grid
  const renderEmployees = () => {
    return (
      <EmployeeGrid
        employees={employees}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        pageInfo={pageInfo}
        handleItemClick={handleItemClick}
        handleGridScroll={handleCombinedGridScroll}
        showLoadingSpinner={showLoadingSpinner}
        shouldHideContent={shouldHideContent}
        loadMoreData={loadMoreData}
        gridRef={gridRef}
        onEdit={handleItemClick}
        onView={handleViewClick}
        onResign={handleResignClick}
        onMarkNoShow={handleMarkNoShowClick}
        onHireWithdraw={handleHireWithdrawClick}
        onRehire={handleRehireClick}
        onManageContract={handleManageContractClick}
      />
    );
  };

  // ADR-113 §5 — the card entries open the SHARED modal router
  // (ContractActionModals below); the submit logic lives there, once. Since
  // ADR-158 §7.4 the card opens PERSON verbs only — Resign, Hire Withdraw,
  // Mark No Show — and everything contract-addressed (Start Delayed included)
  // routes through Manage Contract to the Contracts tab.
  const openContractAction = (employee, action) => {
    setSelectedEmployee(employee);
    setContractAction(action);
  };

  const closeContractAction = () => {
    setContractAction(null);
    setSelectedEmployee(null);
  };

  const handleContractActionCompleted = async () => {
    await fetchEmployeesWithReset();
    closeContractAction();
  };

  // ADR-149 §5.1 — unchanged code, changed status: this is no longer an
  // ADR-113 §5 shortcut into a contract-addressed verb, it is one of Resign's
  // two homes. The repository card was always a person surface.
  // ADR-158 §7.1 — the same for Hire Withdraw and Mark No Show: person verbs,
  // homed here and on the form header's person action area.
  const handleResignClick = (employee) => openContractAction(employee, PERSON_ACTIONS.RESIGN);
  const handleMarkNoShowClick = (employee) => openContractAction(employee, PERSON_ACTIONS.MARK_NO_SHOW);
  const handleHireWithdrawClick = (employee) => openContractAction(employee, PERSON_ACTIONS.HIRE_WITHDRAW);

  // ADR-113 §5 — the launcher route: the contract-addressed actions live on
  // the person's Contracts tab; this deep-links straight to it.
  const handleManageContractClick = (employee) => {
    navigateWithScrollState(employee, `/employees/${employee.id}/edit?tab=CONTRACTS`);
  };

  // Rehire handler
  const handleRehireClick = (employee) => {
    navigateWithScrollState(employee, `/employees/${employee.id}/edit/rehire`);
  };

  // View handler - for employees the user cannot manage
  const handleViewClick = (employee) => {
    navigateWithScrollState(employee, `/employees/${employee.id}/view`);
  };

  // Refresh employee after edit (called from EmployeeForm)
  const refreshEmployee = async (employeeId) => {
    try {
      const updatedEmployee = await employeesApi.getEmployeeById(employeeId);
      updateEmployee(updatedEmployee);
    } catch (err) {
      showAlert(err.message || 'Failed to refresh individual', 'error', err);
    }
  };

  // Check if we're on a positions or roles route
  const isPositionsRoute = location.pathname.includes('/positions');
  const isRolesRoute = location.pathname.includes('/roles');
  
  // If showing the Add Employee form or Employee detail, render the appropriate component
  if (id || isNewItem) {
    // Preserve scroll state through navigation chain
    const scrollState = location.state?.scrollState || location.state?.originalScrollState;

    // If we came from a specific page (e.g. Bench Report), go back there with preserved state
    const returnTo = location.state?.returnTo;
    const backHandler = returnTo
      ? () => navigate(returnTo, { state: { benchState: location.state?.benchState } })
      : isNewItem ? handleCustomBack : handleBackToItems;

    // Handle positions route
    if (isPositionsRoute) {
      return (
        <EmployeePositionHistory
          employeeId={id}
          onBack={() => {
            navigate(`/employees/${id}/edit`, {
              state: {
                ...location.state,
                originalScrollState: scrollState // Preserve scroll state
              }
            });
          }}
          scrollState={scrollState}
          onEmployeeUpdate={() => refreshEmployee(id)}
        />
      );
    }

    // Handle roles route
    if (isRolesRoute) {
      return (
        <EmployeeRoleHistory
          employeeId={id}
          onBack={() => {
            navigate(`/employees/${id}/edit`, {
              state: {
                ...location.state,
                originalScrollState: scrollState // Preserve scroll state
              }
            });
          }}
          scrollState={scrollState}
          onEmployeeUpdate={() => refreshEmployee(id)}
        />
      );
    }

    // ADR-109 §1 — `/employees/:id` is the auto entry (the form resolves Edit/View);
    // `/view` is read-only; everything else (edit, edit/rehire, new) is edit.
    const requestedMode = location.pathname.replace(/\/+$/, '') === `/employees/${id}`
      ? INDIVIDUAL_OPEN_MODES.AUTO
      : location.pathname.includes('/view')
        ? INDIVIDUAL_PAGE_MODES.VIEW
        : INDIVIDUAL_PAGE_MODES.EDIT;

    // Default to EmployeeForm for edit/new/view routes
    return (
      <EmployeeForm
        employeeId={id}
        requestedMode={requestedMode}
        onBack={backHandler}
        onEmployeeUpdate={id ? () => refreshEmployee(id) : null}
        scrollState={scrollState}
      />
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Individuals Repository</Title>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: '16px' }}>
          <Can I={ACTIONS.CREATE} a={SUBJECTS.EMPLOYEE}>
            <HeaderButton onClick={handleAddNewItem}>
              <FaPlus /> Add Individual
            </HeaderButton>
          </Can>
          <HeaderButton onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? <FaEyeSlash /> : <FaEye />}
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </HeaderButton>
        </div>
      </Header>
      <Content>
        <ControlsContainer $show={showFilters}>
          {renderFilters()}
        </ControlsContainer>
        <MainContainer $show={showFilters}>
          <ResultsCount count={pageInfo?.totalCount || 0} singular="individual" plural="individuals" />

          {renderEmployees()}
        </MainContainer>
      </Content>

      {/* ADR-113 §5 — the shared lifecycle modals behind the card shortcuts.
          No episode is passed here: the card path acts on the person's only
          open contract pre-ADR-144 (the Contracts tab mount passes the acting
          episode explicitly). */}
      <ContractActionModals
        action={contractAction}
        employee={selectedEmployee}
        onClose={closeContractAction}
        onCompleted={handleContractActionCompleted}
      />
    </Container>
  );
};

export default EmployeesRepository; 